import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDeal, useDealMutations, useActivityMutations, useLeadMutations } from '../hooks/useCRM';
import ActivityTimeline from '../components/ActivityTimeline';
import DealForm from '../components/DealForm';
import FilesPanel from '../components/FilesPanel';
import SmartActionsBar from '../components/SmartActionsBar';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import '../crm.css';
import { contactPath, dealsPath } from '../utils/crmEnv';
import { getLeadDeals } from '../utils/crmApi';

// Stage Configuration for Progress Bar
const stageConfig = {
    new: { label: 'New', color: '#4338ca', order: 0 },
    qualified: { label: 'Qualified', color: '#1d4ed8', order: 1 },
    proposal: { label: 'Proposal', color: '#b45309', order: 2 },
    site_visit: { label: 'Site Visit', color: '#f97316', order: 3 },
    negotiation: { label: 'Negotiation', color: '#c2410c', order: 4 },
    won: { label: 'Won', color: '#15803d', order: 5 },
    lost: { label: 'Lost', color: '#b91c1c', order: 5 } // Lost is terminal
};

const stagesList = ['new', 'qualified', 'proposal', 'site_visit', 'negotiation', 'won']; // For visual progress

const DealDetailPage = () => {
    const { id } = useParams();
    const history = useHistory();

    // Hooks
    const { deal, loading, error, refetch } = useDeal(id);
    const { update, remove, loading: mutationLoading } = useDealMutations();
    const { create: createActivity } = useActivityMutations();
    const { executeAction } = useLeadMutations();
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();
    const [pastDeals, setPastDeals] = useState([]);
    const [pastDealsLoading, setPastDealsLoading] = useState(false);

    // State
    const [showEditModal, setShowEditModal] = useState(false);
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showOutcomeModal, setShowOutcomeModal] = useState(false);
    const [showSmartFollowupModal, setShowSmartFollowupModal] = useState(false);
    const [showSmartSiteVisitModal, setShowSmartSiteVisitModal] = useState(false);
    const [smartFollowupDraft, setSmartFollowupDraft] = useState({ date: '', time: '' });
    const [smartSiteVisitDraft, setSmartSiteVisitDraft] = useState({ date: '', time: '', location: '', attendees: '', notes: '' });
    const [panelNotes, setPanelNotes] = useState('');
    const [panelNextActionType, setPanelNextActionType] = useState('call');
    const [panelNextActionAt, setPanelNextActionAt] = useState('');

    // Outcome Logic (for buttons)
    const [outcomeStage, setOutcomeStage] = useState(''); // 'won' or 'lost'
    const [lostReason, setLostReason] = useState('');

    const [newActivity, setNewActivity] = useState({
        type: 'call',
        title: '',
        description: '',
        dueDate: ''
    });

    // Formatting
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: includeTime ? '2-digit' : undefined,
                minute: includeTime ? '2-digit' : undefined
            });
        } catch {
            return dateString;
        }
    };

    const formatDateTimeInput = (value) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toISOString().slice(0, 16);
    };

    const normalizePhone = (value) => {
        if (!value) return '';
        return String(value).replace(/[^\d]/g, '');
    };

    const buildTemplateMessage = (templateId) => {
        const name = deal?.contactName || deal?.title || 'there';
        if (templateId === 'intro') {
            return `Hi ${name}, hope you're doing well. Happy to help with your requirements.`;
        }
        if (templateId === 'options') {
            return `Hi ${name}, I have shortlisted some options. When can we discuss?`;
        }
        if (templateId === 'confirm') {
            return `Hi ${name}, confirming your site visit. Please let me know if the time works for you.`;
        }
        return '';
    };

    const handleSmartLogAndOpen = async (channel, title, url, message) => {
        const leadId = deal?.leadId;
        if (!leadId) {
            showError('Link a lead to use Smart Actions');
            return;
        }
        try {
            if (channel === 'call') {
                await executeAction(leadId, { type: 'log_call', note: title }, `deal-smart-call-${leadId}-${Date.now()}`);
            } else if (channel === 'email') {
                await executeAction(leadId, { type: 'send_email', subject: title, body: message || '' }, `deal-smart-email-${leadId}-${Date.now()}`);
            } else if (channel === 'whatsapp') {
                await executeAction(leadId, { type: 'send_whatsapp', message: message || title }, `deal-smart-wa-${leadId}-${Date.now()}`);
            }
        } catch (err) {
            showError('Failed to log communication');
        }

        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
        refetch();
    };

    const openSmartFollowupModal = () => {
        if (!deal?.leadId) {
            showError('Link a lead to use Smart Actions');
            return;
        }
        setSmartFollowupDraft({ date: '', time: '' });
        setShowSmartFollowupModal(true);
    };

    const openSmartSiteVisitModal = () => {
        if (!deal?.leadId) {
            showError('Link a lead to use Smart Actions');
            return;
        }
        setSmartSiteVisitDraft({ date: '', time: '', location: '', attendees: '', notes: '' });
        setShowSmartSiteVisitModal(true);
    };

    const handleSmartFollowupSave = async () => {
        if (!smartFollowupDraft.date) {
            showError('Select a follow-up date');
            return;
        }
        const leadId = deal?.leadId;
        if (!leadId) return;
        const time = smartFollowupDraft.time || '09:00';
        const scheduled = new Date(`${smartFollowupDraft.date}T${time}`);
        if (Number.isNaN(scheduled.getTime())) {
            showError('Invalid follow-up date');
            return;
        }
        try {
            await executeAction(
                leadId,
                { type: 'schedule_follow_up', dueAt: scheduled.getTime() },
                `deal-followup-${leadId}-${scheduled.getTime()}`,
            );
            showSuccess('Follow-up scheduled');
            setShowSmartFollowupModal(false);
            refetch();
        } catch (err) {
            showError('Failed to schedule follow-up');
        }
    };

    const handleSmartSiteVisitSave = async () => {
        if (!smartSiteVisitDraft.date) {
            showError('Select a visit date');
            return;
        }
        const leadId = deal?.leadId;
        if (!leadId) return;
        const dueAt = new Date(`${smartSiteVisitDraft.date}T${smartSiteVisitDraft.time || '09:00'}`).getTime();
        if (Number.isNaN(dueAt)) {
            showError('Invalid visit date');
            return;
        }
        const noteParts = [];
        if (smartSiteVisitDraft.notes) noteParts.push(smartSiteVisitDraft.notes);
        if (smartSiteVisitDraft.attendees) noteParts.push(`Attendees: ${smartSiteVisitDraft.attendees}`);
        const note = noteParts.join('\n') || undefined;

        try {
            await executeAction(
                leadId,
                {
                    type: 'schedule_site_visit',
                    dueAt,
                    location: smartSiteVisitDraft.location || undefined,
                    note,
                    propertyId: deal?.propertyId,
                    propertyTitle: deal?.propertyName || deal?.title,
                },
                `deal-site-visit-${leadId}-${dueAt}`,
            );
            showSuccess('Site visit scheduled');
            setShowSmartSiteVisitModal(false);
            refetch();
        } catch (err) {
            showError('Failed to schedule site visit');
        }
    };

    const handleSmartMarkContacted = async () => {
        const leadId = deal?.leadId;
        if (!leadId) {
            showError('Link a lead to use Smart Actions');
            return;
        }
        try {
            await executeAction(leadId, { type: 'mark_contacted' }, `deal-mark-contacted-${leadId}-${Date.now()}`);
            showSuccess('Lead marked as contacted');
            refetch();
        } catch (err) {
            showError('Failed to update lead');
        }
    };

    const handleSmartTemplateSelect = (templateId) => {
        const message = buildTemplateMessage(templateId);
        if (!message) return;
        const phone = normalizePhone(deal?.contactPhone || deal?.meta?.phone);
        if (!phone) {
            showError('Add a phone number to use templates');
            return;
        }
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        handleSmartLogAndOpen('whatsapp', `WhatsApp template: ${templateId}`, url, message);
    };

    // Handlers
    // Handlers
    const handleDelete = async () => {
        const isConfirmed = await confirm({
            title: 'Delete Deal',
            message: 'Are you sure you want to delete this deal? This cannot be undone.',
            confirmText: 'Delete Deal',
            variant: 'danger'
        });

        if (isConfirmed) {
            try {
                await remove(id);
                showSuccess('Deal deleted successfully');
                history.push(dealsPath());
            } catch (err) {
                console.error('Failed to delete deal:', err);
                showError('Failed to delete deal');
            }
        }
    };

    const handleEditSubmit = async (formData) => {
        try {
            await update(id, formData);
            showSuccess('Deal updated successfully');
            setShowEditModal(false);
            refetch();
        } catch (err) {
            console.error('Failed to update deal:', err);
            showError('Failed to update deal');
        }
    };

    const handleInlineUpdate = async (formData) => {
        try {
            await update(id, formData);
            showSuccess('Deal updated');
            refetch();
        } catch (err) {
            console.error('Failed to update deal:', err);
            showError('Failed to update deal');
        }
    };

    const handleOutcomeClick = (stage) => {
        setOutcomeStage(stage);
        setLostReason('');
        setShowOutcomeModal(true);
    };

    const handleOutcomeSubmit = async () => {
        try {
            await update(id, {
                stage: outcomeStage,
                lostReason: outcomeStage === 'lost' ? lostReason : null,
                actualCloseDate: new Date().toISOString()
            });
            showSuccess(`Deal marked as ${outcomeStage}`);
            setShowOutcomeModal(false);
            refetch();
        } catch (err) {
            console.error('Failed to update deal outcome:', err);
            showError('Failed to update deal outcome');
        }
    };

    const handleActivitySubmit = async () => {
        if (!newActivity.title) return;

        try {
            await createActivity({
                ...newActivity,
                entityType: 'deal',
                entityId: id
            });
            showSuccess('Activity created successfully');
            setShowActivityModal(false);
            setNewActivity({ type: 'call', title: '', description: '', dueDate: '' });
            refetch();
        } catch (err) {
            console.error('Failed to create activity:', err);
            showError('Failed to log activity');
        }
    };

    const handleActivityComplete = async () => {
        refetch();
    };

    const handlePanelStageChange = async (event) => {
        try {
            await update(id, { stage: event.target.value });
            showSuccess('Stage updated');
            refetch();
        } catch (err) {
            showError('Failed to update stage');
        }
    };

    const handlePanelSaveNotes = async () => {
        try {
            await update(id, { notes: panelNotes });
            showSuccess('Notes saved');
            refetch();
        } catch (err) {
            showError('Failed to save notes');
        }
    };

    const handlePanelSchedule = async () => {
        try {
            await update(id, {
                nextActionType: panelNextActionType,
                nextActionAt: panelNextActionAt || null,
                lastActivityAt: new Date().toISOString(),
            });
            showSuccess('Next action scheduled');
            refetch();
        } catch (err) {
            showError('Failed to schedule next action');
        }
    };

    const handlePanelQuickActivity = async (type) => {
        try {
            const activityType = type === 'site_visit' ? 'meeting' : type;
            await createActivity({
                type: activityType,
                title: type === 'site_visit' ? 'Site Visit' : `Deal ${type}`,
                description: '',
                status: 'completed',
                completed: true,
                completedAt: new Date().toISOString(),
                entityType: 'deal',
                entityId: id,
            });
            await update(id, { lastActivityAt: new Date().toISOString() });
            showSuccess('Activity logged');
            refetch();
        } catch (err) {
            showError('Failed to log activity');
        }
    };

    useEffect(() => {
        if (!deal) return;
        setPanelNotes(deal.notes || '');
        setPanelNextActionType(deal.nextActionType || 'call');
        setPanelNextActionAt(formatDateTimeInput(deal.nextActionAt));
    }, [deal]);

    useEffect(() => {
        let active = true;
        const fetchPastDeals = async () => {
            if (!deal?.leadId) {
                if (active) setPastDeals([]);
                return;
            }
            setPastDealsLoading(true);
            try {
                const data = await getLeadDeals(deal.leadId, { includeArchived: true, pageSize: 100 });
                const archived = (data.items || []).filter((item) => item.archived && item.id !== deal.id);
                if (active) setPastDeals(archived);
            } catch (err) {
                if (active) setPastDeals([]);
            } finally {
                if (active) setPastDealsLoading(false);
            }
        };
        fetchPastDeals();
        return () => { active = false; };
    }, [deal?.leadId, deal?.id]);

    // Render Logic
    if (loading) return <div className="crm-loading"><div className="crm-spinner"></div></div>;

    if (error || !deal) return (
        <div className="crm-empty-state">
            <h3>Deal not found</h3>
            <button className="crm-btn crm-btn-primary" onClick={() => history.push(dealsPath())}>Back to Deals</button>
        </div>
    );

    const isClosed = deal.stage === 'won' || deal.stage === 'lost';
    const currentStageIndex = stagesList.indexOf(deal.stage === 'lost' ? 'negotiation' : deal.stage); // Show lost at negotiation step visually maybe? Or just handle differently.
    const displayStage = stageConfig[deal.stage] || { label: deal.stage, color: '#6b7280' };

    // Calculate Days in Pipeline
    const daysInPipeline = Math.floor((new Date() - new Date(deal.createdAt)) / (1000 * 60 * 60 * 24));

    return (
        <div className="crm-container">
            {/* Header / Actions */}
            <div className="crm-page-header">
                <div>
                    <button
                        className="crm-btn crm-btn-link"
                        onClick={() => history.push(dealsPath())}
                        style={{ paddingLeft: 0, marginBottom: '8px' }}
                    >
                        ← Back to Deals
                    </button>
                </div>
                <div className="crm-page-actions">
                    {!isClosed && (
                        <>
                            <button className="crm-btn crm-btn-success" onClick={() => handleOutcomeClick('won')}>Mark Won</button>
                            <button className="crm-btn crm-btn-danger" onClick={() => handleOutcomeClick('lost')}>Mark Lost</button>
                        </>
                    )}
                    <button className="crm-btn crm-btn-secondary" onClick={() => setShowEditModal(true)}>Edit</button>
                    <button className="crm-btn crm-btn-danger" onClick={handleDelete}>Delete</button>
                </div>
            </div>

            {/* Deal Header Card */}
            <div className="crm-card" style={{ marginBottom: '24px' }}>
                <div className="crm-card-body">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                        <div>
                            <h1 style={{ margin: '0 0 8px', fontSize: '24px', color: 'var(--crm-gray-900)' }}>{deal.title}</h1>
                            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--crm-primary)' }}>
                                {formatCurrency(deal.value)}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div className="crm-badge" style={{ background: displayStage.color, color: 'white', fontSize: '14px', marginBottom: '8px' }}>
                                {displayStage.label}
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--crm-gray-600)' }}>
                                Target: {formatDate(deal.expectedCloseDate)}
                            </div>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="crm-progress-container" style={{ marginTop: '24px', marginBottom: '16px' }}>
                        <div className="crm-progress-bar">
                            {stagesList.map((stageKey, index) => {
                                let status = 'pending';
                                if (deal.stage === 'lost') {
                                    status = 'lost'; // Entire bar inactive or special state
                                } else if (deal.stage === 'won') {
                                    status = 'completed';
                                } else {
                                    const currentIndex = stagesList.indexOf(deal.stage);
                                    if (index < currentIndex) status = 'completed';
                                    else if (index === currentIndex) status = 'active';
                                }

                                // Override for visual clarity
                                const color = stageConfig[stageKey].color;
                                let bgColor = 'var(--crm-gray-200)';
                                if (status === 'completed' || status === 'active') bgColor = color;

                                return (
                                    <div key={stageKey} style={{
                                        flex: 1,
                                        height: '8px',
                                        background: bgColor,
                                        marginRight: '4px',
                                        borderRadius: '4px',
                                        opacity: status === 'pending' ? 0.3 : 1
                                    }} title={stageConfig[stageKey].label} />
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: 'var(--crm-gray-500)' }}>
                            {stagesList.map(s => <span key={s}>{stageConfig[s].label}</span>)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Layout Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                {/* Details */}
                <div className="crm-card">
                    <div className="crm-card-header"><h2 className="crm-card-title">Deal Information</h2></div>
                    <div className="crm-card-body">
                        <div className="crm-form-group">
                            <span className="crm-label">Contact</span>
                            {deal.contactId ? (
                                <button
                                    className="crm-btn crm-btn-link"
                                    style={{ padding: 0 }}
                                    onClick={() => history.push(contactPath(deal.contactId))}
                                >
                                    {deal.contactName || 'View Contact'}
                                </button>
                            ) : (
                                <span>{deal.contactName || '-'}</span>
                            )}
                        </div>
                        <div className="crm-form-group">
                            <span className="crm-label">Description</span>
                            <div style={{ whiteSpace: 'pre-wrap', color: 'var(--crm-gray-900)' }}>{deal.description || '-'}</div>
                        </div>
                        <div className="crm-form-group">
                            <span className="crm-label">Created</span>
                            <span style={{ color: 'var(--crm-gray-900)' }}>{formatDate(deal.createdAt)} ({daysInPipeline} days ago)</span>
                        </div>
                        {deal.leadId && (
                            <div className="crm-form-group">
                                <span className="crm-label">Past Deals</span>
                                {pastDealsLoading && <div className="crm-muted">Loading...</div>}
                                {!pastDealsLoading && pastDeals.length === 0 && (
                                    <div className="crm-muted">No past deals.</div>
                                )}
                                {!pastDealsLoading && pastDeals.length > 0 && (
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {pastDeals.map((item) => (
                                            <div key={item.id} className="crm-card" style={{ padding: '10px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{item.title || 'Untitled Deal'}</div>
                                                        <div className="crm-help-text">Archived {item.archivedAt ? formatDate(item.archivedAt) : 'Previously'}</div>
                                                    </div>
                                                    <span className="crm-badge crm-badge-muted">Archived</span>
                                                </div>
                                                <div style={{ marginTop: '8px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                                    <div>
                                                        <div className="crm-field-label">Value</div>
                                                        <div className="crm-field-value">{formatCurrency(item.value)}</div>
                                                    </div>
                                                    <div>
                                                        <div className="crm-field-label">Expected close</div>
                                                        <div className="crm-field-value">
                                                            {item.expectedCloseAt ? formatDate(item.expectedCloseAt) : item.expectedCloseDate ? formatDate(item.expectedCloseDate) : '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ marginTop: '8px' }}>
                                                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={() => history.push(dealPath(item.id))}>
                                                        Open Deal
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {deal.stage === 'lost' && (
                            <div className="crm-form-group">
                                <span className="crm-label" style={{ color: 'var(--crm-danger)' }}>Lost Reason</span>
                                <div>{deal.lostReason || '-'}</div>
                            </div>
                        )}
                        {deal.actualCloseDate && (
                            <div className="crm-form-group">
                                <span className="crm-label">Closed On</span>
                                <span>{formatDate(deal.actualCloseDate)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Deal Actions */}
                <div className="crm-card">
                    <div className="crm-card-header"><h2 className="crm-card-title">Deal Actions</h2></div>
                    <div className="crm-card-body">
                        <DealForm
                            deal={deal}
                            onSubmit={handleInlineUpdate}
                            onCancel={() => {}}
                            loading={mutationLoading}
                            showCancel={false}
                        />
                    </div>
                </div>
            </div>

            {/* Activity Timeline */}
            <div className="crm-card" style={{ marginBottom: '24px' }}>
                <div className="crm-card-header">
                    <h2 className="crm-card-title">Files</h2>
                </div>
                <div className="crm-card-body">
                    <FilesPanel entityType="deal" entityId={id} />
                </div>
            </div>

            {/* Activity Timeline */}
            <div className="crm-card">
                <div className="crm-card-header">
                    <h2 className="crm-card-title">Activities</h2>
                    <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={() => setShowActivityModal(true)}>+ Add Activity</button>
                </div>
                <div className="crm-card-body">
                    <ActivityTimeline activities={deal.activities || []} onComplete={handleActivityComplete} />
                </div>
            </div>

            {/* Modals */}
            {showEditModal && (
                <div className="crm-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', width: '100%', background: 'var(--crm-bg)' }}>
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Edit Deal</h2>
                            <button className="crm-modal-close" onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <div className="crm-modal-body">
                            <DealForm deal={deal} onSubmit={handleEditSubmit} onCancel={() => setShowEditModal(false)} loading={mutationLoading} />
                        </div>
                    </div>
                </div>
            )}

            {showOutcomeModal && (
                <div className="crm-modal-overlay" onClick={() => setShowOutcomeModal(false)}>
                    <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ background: 'var(--crm-bg)' }}>
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">{outcomeStage === 'won' ? 'Mark Won' : 'Mark Lost'}</h2>
                            <button className="crm-modal-close" onClick={() => setShowOutcomeModal(false)}>×</button>
                        </div>
                        <div className="crm-modal-body">
                            {outcomeStage === 'won' ? (
                                <p>Confirm moving this deal to Won?</p>
                            ) : (
                                <div className="crm-form-group">
                                    <label className="crm-label required">Reason for Loss</label>
                                    <textarea
                                        className="crm-textarea"
                                        value={lostReason}
                                        onChange={(e) => setLostReason(e.target.value)}
                                        rows={3}
                                    />
                                </div>
                            )}
                            <div className="crm-form-actions">
                                <button className="crm-btn crm-btn-secondary" onClick={() => setShowOutcomeModal(false)}>Cancel</button>
                                <button
                                    className={`crm-btn ${outcomeStage === 'won' ? 'crm-btn-success' : 'crm-btn-danger'}`}
                                    onClick={handleOutcomeSubmit}
                                    disabled={outcomeStage === 'lost' && !lostReason}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Activity Modal (Same as Contacts) */}
            {showActivityModal && (
                <div className="crm-modal-overlay" onClick={() => setShowActivityModal(false)}>
                    <div className="crm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', background: 'var(--crm-bg)' }}>
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Log Activity</h2>
                            <button className="crm-modal-close" onClick={() => setShowActivityModal(false)}>×</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-form-group">
                                <label className="crm-label">Smart Actions</label>
                                <SmartActionsBar
                                    entityType="lead"
                                    entityId={deal?.leadId || ''}
                                    lead={{ phone: deal?.contactPhone || deal?.meta?.phone, email: deal?.meta?.email }}
                                    disabledReason={deal?.leadId ? undefined : 'Link a lead to enable'}
                                    onCall={() => {
                                        const phone = normalizePhone(deal?.contactPhone || deal?.meta?.phone);
                                        handleSmartLogAndOpen('call', 'Call lead', phone ? `tel:${phone}` : '');
                                    }}
                                    onWhatsApp={() => {
                                        const phone = normalizePhone(deal?.contactPhone || deal?.meta?.phone);
                                        const url = phone ? `https://wa.me/${phone}` : '';
                                        handleSmartLogAndOpen('whatsapp', 'WhatsApp lead', url);
                                    }}
                                    onEmail={() => {
                                        const email = deal?.meta?.email;
                                        handleSmartLogAndOpen('email', 'Email lead', email ? `mailto:${email}` : '', '');
                                    }}
                                    onScheduleFollowUp={openSmartFollowupModal}
                                    onScheduleSiteVisit={openSmartSiteVisitModal}
                                    onMarkContacted={handleSmartMarkContacted}
                                    onTemplateSelect={handleSmartTemplateSelect}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Type</label>
                                <select className="crm-select" value={newActivity.type} onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}>
                                    <option value="call">Phone Call</option>
                                    <option value="email">Email</option>
                                    <option value="meeting">Meeting</option>
                                    <option value="task">Task</option>
                                    <option value="note">Note</option>
                                </select>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label required">Title</label>
                                <input type="text" className="crm-input" value={newActivity.title} onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })} />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Description</label>
                                <textarea className="crm-textarea" value={newActivity.description} onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })} />
                            </div>
                            <div className="crm-form-group"><label className="crm-label">Due Date</label><input type="datetime-local" className="crm-input" value={newActivity.dueDate} onChange={(e) => setNewActivity({ ...newActivity, dueDate: e.target.value })} /></div>
                            <div className="crm-form-actions">
                                <button className="crm-btn crm-btn-secondary" onClick={() => setShowActivityModal(false)}>Cancel</button>
                                <button className="crm-btn crm-btn-primary" onClick={handleActivitySubmit}>Save Activity</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {showSmartFollowupModal && (
                <div className="crm-modal-overlay" onClick={() => setShowSmartFollowupModal(false)}>
                    <div className="crm-modal" onClick={(event) => event.stopPropagation()} style={{ background: 'var(--crm-bg)', maxWidth: '420px', width: '100%' }}>
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Schedule Follow-up</h2>
                            <button className="crm-modal-close" onClick={() => setShowSmartFollowupModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-form-row">
                                <div className="crm-form-group">
                                    <label className="crm-label required">Date</label>
                                    <input
                                        type="date"
                                        className="crm-input"
                                        value={smartFollowupDraft.date}
                                        onChange={(event) => setSmartFollowupDraft((prev) => ({ ...prev, date: event.target.value }))}
                                    />
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Time</label>
                                    <input
                                        type="time"
                                        className="crm-input"
                                        value={smartFollowupDraft.time}
                                        onChange={(event) => setSmartFollowupDraft((prev) => ({ ...prev, time: event.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className="crm-form-actions">
                                <button className="crm-btn crm-btn-secondary" onClick={() => setShowSmartFollowupModal(false)}>Cancel</button>
                                <button className="crm-btn crm-btn-primary" onClick={handleSmartFollowupSave}>Save Follow-up</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSmartSiteVisitModal && (
                <div className="crm-modal-overlay" onClick={() => setShowSmartSiteVisitModal(false)}>
                    <div className="crm-modal" onClick={(event) => event.stopPropagation()} style={{ background: 'var(--crm-bg)', maxWidth: '520px', width: '100%' }}>
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">Schedule Site Visit</h2>
                            <button className="crm-modal-close" onClick={() => setShowSmartSiteVisitModal(false)}>X</button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-form-row">
                                <div className="crm-form-group">
                                    <label className="crm-label required">Date</label>
                                    <input
                                        type="date"
                                        className="crm-input"
                                        value={smartSiteVisitDraft.date}
                                        onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, date: event.target.value }))}
                                    />
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Time</label>
                                    <input
                                        type="time"
                                        className="crm-input"
                                        value={smartSiteVisitDraft.time}
                                        onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, time: event.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Location</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={smartSiteVisitDraft.location}
                                    onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, location: event.target.value }))}
                                    placeholder="Property address or meeting point"
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Attendees</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={smartSiteVisitDraft.attendees}
                                    onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, attendees: event.target.value }))}
                                    placeholder="comma separated names"
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Notes</label>
                                <textarea
                                    className="crm-textarea"
                                    rows={4}
                                    value={smartSiteVisitDraft.notes}
                                    onChange={(event) => setSmartSiteVisitDraft((prev) => ({ ...prev, notes: event.target.value }))}
                                    placeholder="Any notes for the visit"
                                />
                            </div>
                            <div className="crm-form-actions">
                                <button className="crm-btn crm-btn-secondary" onClick={() => setShowSmartSiteVisitModal(false)}>Cancel</button>
                                <button className="crm-btn crm-btn-primary" onClick={handleSmartSiteVisitSave}>Save Visit</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDialog />
        </div>
    );
};

export default DealDetailPage;
